import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Search, X } from "lucide-react";
import VehicleCard from "./VehicleCard";
import VehicleDetail from "./TrackingPanel";

export default function VehiclePanel({
  vehicles,
  selectedVehicle,
  onSelectVehicle,
  onRemoveVehicle,
  onAddVehicle,
  onTrackVehicle,
  onStopTrackVehicle,
  onRefreshVehicle,
  onForceRefreshVehicle,
  onSetManualLocationVehicle,
  onTollClick,
  activeTollId,
  searchQuery,
  estimatedPositions,
  onShareVehicle,
  onViewProfile,
}) {
  const [localSearch, setLocalSearch] = useState("");
  const q = (searchQuery || localSearch).toLowerCase();

  const filtered = vehicles.filter(v => {
    if (!q) return true;
    return [v.nickname, v.plateNumber, v.location, v.type, v.ownerName]
      .some(s => s?.toLowerCase().includes(q));
  });

  const lowBalance = vehicles.filter(v => v.fastagBalance > 0 && v.fastagBalance < 300);

  return (
    <aside style={{
      width: "100%",
      height: "100%",
      background: "#ffffff",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>
      <AnimatePresence mode="wait">
        {selectedVehicle ? (
          <motion.div
            key="detail"
            initial={{ opacity:0, x:24 }}
            animate={{ opacity:1, x:0 }}
            exit={{    opacity:0, x:24 }}
            transition={{ duration:0.2, ease:"easeOut" }}
            style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}
          >
            <VehicleDetail
              vehicle={selectedVehicle}
              onBack={() => onSelectVehicle(null)}
              onTollClick={onTollClick}
              activeTollId={activeTollId}
              onRefresh={onRefreshVehicle}
              onForceRefresh={onForceRefreshVehicle}
              onSetManualLocation={onSetManualLocationVehicle}
              estimatedPos={estimatedPositions?.[selectedVehicle?.id]}
            />
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity:0, x:-24 }}
            animate={{ opacity:1, x:0 }}
            exit={{    opacity:0, x:-24 }}
            transition={{ duration:0.2, ease:"easeOut" }}
            style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}
          >
            {/* Header */}
            <div style={{
              padding: "22px 18px 16px",
              background: "#fff",
              borderBottom: "1px solid #EBEBEB",
              flexShrink: 0,
            }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
                <div>
                  <div style={{ fontSize:19, fontWeight:700, color:"#111827", letterSpacing:-0.2 }}>Fleet</div>
                  <div style={{ fontSize:13, color:"#9CA3AF", marginTop:1 }}>
                    {vehicles.length === 0 ? "No vehicles yet" : `${vehicles.length} vehicle${vehicles.length > 1 ? "s" : ""}`}
                  </div>
                </div>
                <button
                  onClick={onAddVehicle}
                  style={{
                    display:"flex", alignItems:"center", gap:6,
                    background:"#111827", color:"#fff",
                    border:"none", borderRadius:9, padding:"10px 18px",
                    fontSize:14, fontWeight:600, cursor:"pointer",
                    boxShadow:"0 1px 4px rgba(0,0,0,0.18)",
                    transition:"background 0.15s",
                  }}
                  onMouseOver={e => e.currentTarget.style.background="#1F2937"}
                  onMouseOut={e  => e.currentTarget.style.background="#111827"}
                >
                  <Plus size={14} strokeWidth={2.5} />
                  Add Vehicle
                </button>
              </div>

              {/* Search */}
              {vehicles.length > 0 && (
                <div style={{ position:"relative" }}>
                  <Search
                    size={13} color="#9CA3AF"
                    style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}
                  />
                  <input
                    value={localSearch}
                    onChange={e => setLocalSearch(e.target.value)}
                    placeholder="Search plate, name, location…"
                    style={{
                      width:"100%", boxSizing:"border-box",
                      height:40, paddingLeft:34, paddingRight: localSearch ? 34 : 12,
                      background:"#F5F5F5", border:"1.5px solid transparent", borderRadius:9,
                      fontSize:13, color:"#111827", outline:"none",
                      transition:"border-color 0.15s, background 0.15s",
                    }}
                    onFocus={e  => { e.target.style.borderColor="#111827"; e.target.style.background="#fff"; }}
                    onBlur={e   => { e.target.style.borderColor="transparent"; e.target.style.background="#F5F5F5"; }}
                  />
                  {localSearch && (
                    <button
                      onClick={() => setLocalSearch("")}
                      style={{
                        position:"absolute", right:9, top:"50%", transform:"translateY(-50%)",
                        background:"none", border:"none", cursor:"pointer", color:"#9CA3AF",
                        padding:2, display:"flex",
                      }}
                    ><X size={12} /></button>
                  )}
                </div>
              )}
            </div>

            {/* Vehicle list */}
            <div style={{ flex:1, overflowY:"auto", padding:"14px 14px 10px", display:"flex", flexDirection:"column", gap:8 }}>
              {vehicles.length === 0 ? (
                <div style={{ textAlign:"center", padding:"56px 24px" }}>
                  <div style={{
                    width:60, height:60, borderRadius:14, background:"#F3F4F6",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    margin:"0 auto 14px",
                  }}>
                    <Plus size={22} color="#D1D5DB" />
                  </div>
                  <div style={{ fontSize:16, fontWeight:600, color:"#374151", marginBottom:4 }}>
                    No vehicles yet
                  </div>
                  <div style={{ fontSize:13, color:"#9CA3AF", lineHeight:1.5 }}>
                    Add your first vehicle to start tracking its FASTag data.
                  </div>
                </div>
              ) : filtered.length === 0 ? (
                <div style={{ textAlign:"center", padding:"40px 20px" }}>
                  <div style={{ fontSize:13, color:"#9CA3AF" }}>No vehicles match your search</div>
                </div>
              ) : (
                <AnimatePresence>
                  {filtered.map((vehicle, i) => (
                    <motion.div
                      key={vehicle.id}
                      initial={{ opacity:0, y:8 }}
                      animate={{ opacity:1, y:0 }}
                      exit={{    opacity:0, scale:0.97 }}
                      transition={{ delay: i * 0.025, duration:0.18 }}
                    >
                      <VehicleCard
                        vehicle={vehicle}
                        isSelected={selectedVehicle?.id === vehicle.id}
                        onClick={() => onSelectVehicle(vehicle)}
                        onRemove={onRemoveVehicle}
                        onTrack={onTrackVehicle}
                        onStopTrack={onStopTrackVehicle}
                        onRefresh={onRefreshVehicle}
                        onShare={onShareVehicle}
                        onViewProfile={onViewProfile}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </div>

            {/* Low balance footer */}
            {lowBalance.length > 0 && (
              <div style={{
                padding:"10px 14px", borderTop:"1px solid #EBEBEB",
                background:"#FFFBEB", flexShrink:0,
                display:"flex", alignItems:"center", gap:8,
              }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background:"#D97706", flexShrink:0 }} />
                <span style={{ fontSize:13, fontWeight:600, color:"#92400E" }}>
                  {lowBalance.length} vehicle{lowBalance.length > 1 ? "s" : ""} with low FASTag balance
                </span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </aside>
  );
}
